import { useCallback, useState } from 'react';
import type { BusinessTypeRef, BusinessTypeDraft } from '@domain/entities';
import { useContainer } from '../providers/container-context';
import { useToast } from '../providers/toast-context';
import { useAuth } from '../providers/auth-context';
import { useAsync } from '../hooks/use-async';
import { useRealtimeVersion } from '../providers/realtime-context';
import { useIsMobile } from '../hooks/use-is-mobile';
import { ConfirmModal, ErrorState, Modal, RefreshButton, StatusBadge, TableSkeleton, TextField } from '../components/ui';

const NEW_DRAFT: BusinessTypeDraft = { slug: '', label: '', sortOrder: 0 };

export function BusinessTypesPage(): JSX.Element {
  const container = useContainer();
  const toast = useToast();
  const { can } = useAuth();
  const vBusinessTypes = useRealtimeVersion('business-types');

  const isMobile = useIsMobile();
  const types = useAsync(() => container.businessTypes.list.execute(), [container, vBusinessTypes]);
  const [editorId, setEditorId] = useState<string | null | undefined>(undefined);
  const [draft, setDraft] = useState<BusinessTypeDraft>(NEW_DRAFT);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<{ title: string; body: string; confirmLabel?: string; danger?: boolean; fn: () => void } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const openEditor = useCallback((type: BusinessTypeRef | null) => {
    if (type === null) { setEditorId(null); setDraft(NEW_DRAFT); return; }
    setEditorId(type.id);
    setDraft({ slug: type.slug, label: type.label, sortOrder: type.sortOrder });
  }, []);

  const save = useCallback(async () => {
    if (editorId === undefined) return;
    setSaving(true);
    try {
      await container.businessTypes.save.execute({ id: editorId, draft });
      toast.notify(editorId === null ? 'Type ajouté' : 'Type mis à jour');
      setEditorId(undefined);
      types.reload();
    } catch (error) {
      toast.notifyError(error);
    } finally {
      setSaving(false);
    }
  }, [container, editorId, draft, toast, types]);

  const toggle = useCallback(async (type: BusinessTypeRef) => {
    try {
      await container.businessTypes.toggle.execute({ id: type.id, currentIsActive: type.isActive });
      types.reload();
    } catch (error) { toast.notifyError(error); }
  }, [container, toast, types]);

  const remove = useCallback(async (id: string) => {
    try {
      await container.businessTypes.remove.execute(id);
      toast.notify('Type supprimé');
      types.reload();
    } catch (error) { toast.notifyError(error); }
  }, [container, toast, types]);

  const rows = types.data ?? [];

  return (
    <>
      <div className="bbm-toolbar">
        <div className="bbm-toolbar__start">
          <div className="bbm-toolbar__hint">
            Types d'entreprises disponibles dans le répertoire
          </div>
        </div>
        <div className="bbm-toolbar__end">
          <button
            type="button"
            className="bbm-btn bbm-btn--primary"
            disabled={!can('companies', 'create')}
            title={can('companies', 'create') ? '' : 'Réservé au super-admin'}
            onClick={() => openEditor(null)}
          >
            + Ajouter un type
          </button>
          <RefreshButton onRefresh={types.reload} loading={types.loading} />
        </div>
      </div>

      {types.loading ? <TableSkeleton rows={6} /> : null}
      {types.error ? <ErrorState status={types.error.status} message={types.error.message} onRetry={types.reload} /> : null}

      {rows.length > 0 ? (
        isMobile ? (
          <div className="bbm-card-stack">
            {rows.map((type) => (
              <div key={type.id} className="bbm-mobile-card">
                <div className="bbm-mobile-card__head">
                  <div>
                    <div className="bbm-mobile-card__title">{type.label}</div>
                    <div className="bbm-mobile-card__sub" style={{ fontFamily: 'monospace' }}>{type.slug}</div>
                  </div>
                  <StatusBadge status={type.isActive ? 'approved' : 'inactive'} label={type.isActive ? 'Actif' : 'Inactif'} />
                </div>
                <div className="bbm-mobile-card__fields">
                  <div className="bbm-mobile-card__field">
                    <span className="bbm-mobile-card__label">Ordre</span>
                    <span>{type.sortOrder}</span>
                  </div>
                </div>
                <div className="bbm-mobile-card__actions">
                  <button type="button" className="bbm-btn" disabled={!can('companies', 'edit')} onClick={() => setPending({
                    title: 'Modifier la visibilité ?',
                    body: 'La visibilité de ce type sera modifiée.',
                    fn: () => void toggle(type),
                  })}>{type.isActive ? 'Désactiver' : 'Activer'}</button>
                  <button type="button" className="bbm-btn" disabled={!can('companies', 'edit')} onClick={() => openEditor(type)}>Modifier</button>
                  <button type="button" className="bbm-btn bbm-btn--danger" disabled={!can('companies', 'delete')} onClick={() => setPending({
                    title: 'Supprimer ce type ?',
                    body: 'Cette action est irréversible.',
                    confirmLabel: 'Supprimer',
                    danger: true,
                    fn: () => void remove(type.id),
                  })}>Supprimer</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bbm-panel">
            <div className="bbm-row bbm-row--head">
              <div style={{ width: 60 }}>Ordre</div>
              <div style={{ width: 200 }}>Libellé</div>
              <div style={{ width: 200 }}>Slug</div>
              <div style={{ width: 100 }}>Statut</div>
              <div style={{ marginLeft: 'auto' }}>Actions</div>
            </div>
            {rows.map((type) => (
              <div key={type.id} className="bbm-row">
                <div className="bbm-cell" style={{ width: 60 }}>{type.sortOrder}</div>
                <div className="bbm-cell-strong" style={{ width: 200 }}>{type.label}</div>
                <div className="bbm-cell" style={{ width: 200, fontFamily: 'monospace', fontSize: 12 }}>{type.slug}</div>
                <div style={{ width: 100 }}>
                  <StatusBadge status={type.isActive ? 'approved' : 'inactive'} label={type.isActive ? 'Actif' : 'Inactif'} />
                </div>
                <div className="bbm-row__actions">
                  <button type="button" className="bbm-btn" disabled={!can('companies', 'edit')} onClick={() => setPending({
                    title: 'Modifier la visibilité ?',
                    body: 'La visibilité de ce type sera modifiée.',
                    fn: () => void toggle(type),
                  })}>
                    {type.isActive ? 'Désactiver' : 'Activer'}
                  </button>
                  <button type="button" className="bbm-btn" disabled={!can('companies', 'edit')} onClick={() => openEditor(type)}>Modifier</button>
                  <button type="button" className="bbm-btn bbm-btn--danger" disabled={!can('companies', 'delete')} onClick={() => setPending({
                    title: 'Supprimer ce type ?',
                    body: 'Cette action est irréversible.',
                    confirmLabel: 'Supprimer',
                    danger: true,
                    fn: () => void remove(type.id),
                  })}>
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : null}

      {editorId !== undefined ? (
        <Modal
          title={editorId === null ? 'Nouveau type' : 'Modifier le type'}
          width={480}
          onClose={() => setEditorId(undefined)}
          onSubmit={() => void save()}
          busy={saving}
        >
          <TextField label="Libellé" value={draft.label} onChange={(v) => setDraft({ ...draft, label: v })} />
          <TextField label="Slug (lettres minuscules, tirets)" value={draft.slug} onChange={(v) => setDraft({ ...draft, slug: v })} />
          <TextField label="Ordre d'affichage" value={String(draft.sortOrder)} onChange={(v) => setDraft({ ...draft, sortOrder: Number(v) || 0 })} />
        </Modal>
      ) : null}
      {pending !== null ? (
        <ConfirmModal
          title={pending.title}
          body={pending.body}
          confirmLabel={pending.confirmLabel}
          danger={pending.danger}
          busy={actionBusy}
          onConfirm={() => {
            setActionBusy(true);
            Promise.resolve(pending.fn()).finally(() => {
              setActionBusy(false);
              setPending(null);
            });
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}
    </>
  );
}
